import { QualityInspection } from "@/data/mockData";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Clock,
  FileText,
  Package,
  Calendar,
  User,
  Edit,
  Download,
  Printer
} from "lucide-react";

interface QualityInspectionDetailsProps {
  inspection: QualityInspection;
  onEdit: () => void;
  onClose: () => void;
}

export const QualityInspectionDetails = ({ 
  inspection, 
  onEdit, 
  onClose 
}: QualityInspectionDetailsProps) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Passed":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "Failed":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "Pending":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case "In Progress":
        return <AlertTriangle className="h-5 w-5 text-blue-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "Passed":
        return "default";
      case "Failed":
        return "destructive";
      case "Pending":
        return "secondary";
      case "In Progress":
        return "outline";
      default:
        return "outline";
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "Critical":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "Major":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case "Minor":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getOverallScore = () => {
    const totalItems = inspection.inspectedItems.length;
    if (totalItems === 0) return 0;
    
    const passedItems = inspection.inspectedItems.filter(item => item.status === "Passed").length;
    return Math.round((passedItems / totalItems) * 100);
  };

  const getDefectSummary = () => {
    const allDefects = inspection.inspectedItems.flatMap(item => item.defects);
    const criticalDefects = allDefects.filter(d => d.severity === "Critical").length;
    const majorDefects = allDefects.filter(d => d.severity === "Major").length;
    const minorDefects = allDefects.filter(d => d.severity === "Minor").length;
    
    return { criticalDefects, majorDefects, minorDefects, totalDefects: allDefects.length };
  };

  const defectSummary = getDefectSummary();
  const overallScore = getOverallScore();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{inspection.inspectionId}</h2>
            <p className="text-muted-foreground">Quality Inspection Report</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button size="sm" onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {/* Status and Score */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {getStatusIcon(inspection.overallStatus)}
              <div>
                <p className="text-sm text-muted-foreground">Overall Status</p>
                <Badge variant={getStatusVariant(inspection.overallStatus) as any} className="text-sm">
                  {inspection.overallStatus}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-primary">{overallScore}%</span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pass Rate</p>
                <p className="text-lg font-semibold">{overallScore}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-red-600">{defectSummary.totalDefects}</span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Defects</p>
                <p className="text-lg font-semibold">{defectSummary.totalDefects}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="defects">Defects</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Inspection Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Inspection ID</span>
                  <span className="font-medium">{inspection.inspectionId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">PO Number</span>
                  <span className="font-medium">{inspection.poNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Inspector</span>
                  <span className="font-medium">{inspection.inspectorName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Inspection Date</span>
                  <span className="font-medium">
                    {new Date(inspection.inspectionDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={getStatusVariant(inspection.overallStatus) as any}>
                    {inspection.overallStatus}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Items Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Items</span>
                  <span className="font-medium">{inspection.inspectedItems.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Passed Items</span>
                  <span className="font-medium text-green-600">
                    {inspection.inspectedItems.filter(item => item.status === "Passed").length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Failed Items</span>
                  <span className="font-medium text-red-600">
                    {inspection.inspectedItems.filter(item => item.status === "Failed").length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">In Progress</span>
                  <span className="font-medium text-blue-600">
                    {inspection.inspectedItems.filter(item => item.status === "In Progress").length}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {inspection.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm bg-muted/50 p-3 rounded">{inspection.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="space-y-4">
          <div className="space-y-4">
            {inspection.inspectedItems.map((item, index) => (
              <Card key={item.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{item.skuName}</CardTitle>
                        <CardDescription>{item.skuCode}</CardDescription>
                      </div>
                    </div>
                    <Badge variant={getStatusVariant(item.status) as any}>
                      {item.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Quantity Ordered</p>
                      <p className="font-medium">{item.quantityOrdered}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Quantity Inspected</p>
                      <p className="font-medium">{item.quantityInspected}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Quantity Passed</p>
                      <p className="font-medium text-green-600">{item.quantityPassed}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Quantity Failed</p>
                      <p className="font-medium text-red-600">{item.quantityFailed}</p>
                    </div>
                  </div>
                  
                  {item.notes && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground">Item Notes</p>
                      <p className="text-sm bg-muted/50 p-2 rounded mt-1">{item.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Defects Tab */}
        <TabsContent value="defects" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{defectSummary.criticalDefects}</div>
                <p className="text-sm text-muted-foreground">Critical Defects</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{defectSummary.majorDefects}</div>
                <p className="text-sm text-muted-foreground">Major Defects</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{defectSummary.minorDefects}</div>
                <p className="text-sm text-muted-foreground">Minor Defects</p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {inspection.inspectedItems
              .filter(item => item.defects.length > 0)
              .map((item) => (
                <Card key={item.id}>
                  <CardHeader>
                    <CardTitle className="text-base">{item.skuName}</CardTitle>
                    <CardDescription>{item.skuCode}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {item.defects.map((defect, defectIndex) => (
                        <div key={defect.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{defect.type || "Unspecified Defect"}</h4>
                            <Badge className={getSeverityColor(defect.severity)}>
                              {defect.severity}
                            </Badge>
                          </div>
                          {defect.description && (
                            <p className="text-sm text-muted-foreground">{defect.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Inspection Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Quality Metrics</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Overall Pass Rate</span>
                      <span className="font-medium">{overallScore}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Items Inspected</span>
                      <span className="font-medium">{inspection.inspectedItems.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Items Passed</span>
                      <span className="font-medium text-green-600">
                        {inspection.inspectedItems.filter(item => item.status === "Passed").length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Items Failed</span>
                      <span className="font-medium text-red-600">
                        {inspection.inspectedItems.filter(item => item.status === "Failed").length}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Defect Analysis</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total Defects</span>
                      <span className="font-medium">{defectSummary.totalDefects}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Critical Defects</span>
                      <span className="font-medium text-red-600">{defectSummary.criticalDefects}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Major Defects</span>
                      <span className="font-medium text-orange-600">{defectSummary.majorDefects}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Minor Defects</span>
                      <span className="font-medium text-yellow-600">{defectSummary.minorDefects}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-2">Recommendations</h4>
                <div className="space-y-2 text-sm">
                  {overallScore >= 90 ? (
                    <p className="text-green-600">✓ Quality standards met. Items can be accepted.</p>
                  ) : overallScore >= 70 ? (
                    <p className="text-yellow-600">⚠ Quality below standards. Review failed items before acceptance.</p>
                  ) : (
                    <p className="text-red-600">✗ Quality standards not met. Reject items and contact vendor.</p>
                  )}
                  
                  {defectSummary.criticalDefects > 0 && (
                    <p className="text-red-600">⚠ Critical defects found. Immediate vendor notification required.</p>
                  )}
                  
                  {defectSummary.majorDefects > 3 && (
                    <p className="text-orange-600">⚠ High number of major defects. Consider vendor performance review.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
